import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import cx from 'classnames'

import Asset, { TransitionAsset } from 'models/Asset'
import { assetToTimelineDragElement } from 'components/Timeline/DragAndDrop/lib/assetToTimelineDragElement'
import { DraggingItemContext } from 'components/Timeline/DraggingItemProvider'
import { TimelineScrollPositionContext } from 'components/Timeline/ScrollPositionContext'
import { TimelineLeftOffsetContext } from 'contexts/TimelineLeftOffsetContext'
import { TimelineTopOffsetContext } from 'components/Timeline/TimelineTopOffsetContext'
import * as Selectors from 'selectors'
import { isEmpty } from 'lodash'
import { XYCoord } from 'react-dnd'
import { getLayerIndexFromY } from 'Util/timeline-coords'
import { time2Pixel } from 'Util'
import { snapToLayer } from 'components/Timeline/DragAndDrop/lib/snapToLayer'
import styles from './dropHover.module.scss'


type Props = {
  item: { id: string, type: string, selectedDragIds?: [string], selectedItems?: [string] }
  clientOffset: XYCoord,
  sourceOffset: XYCoord,
  initialClientOffset: XYCoord,
  initialSourceOffset: XYCoord,
  hoveredAssetId: string,
  ignoredTypes?: string[],
  isOverNewLayer: boolean,
  insertBetween: {
    startTime: number,
    endTime: number,
    layerId: string,
    layerIndex: number,
    duration: number,
  },
  hoveredTransitions: string[],
}

function DropHoverOnAssets({ item, ignoredTypes = [], insertBetween,
  clientOffset: { x: clientX, y: clientY }, hoveredAssetId, hoveredTransitions, isOverNewLayer }: Props) {
  const timelineTopOffset = useContext(TimelineTopOffsetContext)
  const { timelineLeftOffset } = useContext(TimelineLeftOffsetContext)
  const { scrollLeft, scrollTop } = useContext(TimelineScrollPositionContext)
  const { setProps: setDraggingItemProps, getProps } = useContext(DraggingItemContext)

  const scale = useSelector((state: RootState) => state.timeline.scale)
  const draggingAsset = useSelector(state => Selectors.selectAssetById(state, item.id))
  const draggingFile = useSelector(state => Selectors.getSourceFileById(state, item.id))
  const asset = draggingAsset || draggingFile
  const { normalizedSourceX, detachedMode } = getProps()
  const draggingAssetWidth = useMemo(() => time2Pixel(asset?.duration ?? 0, scale), [ asset, scale ])

  const transitions = useSelector(Selectors.getTransitionAssets)

  const width = asset ? assetToTimelineDragElement(scale)(asset).width : 0
  const assetsByLayers = useSelector(Selectors.getAssetsByLayers)

  const timelineDragElementsLayers = useMemo(() => Array.from(
    assetsByLayers,
    ([ , value ]) => (value as (Asset & { type: string })[])
      .filter(a => a.id !== item?.id)
      .filter(a => !(a instanceof TransitionAsset))
      .map(a => ({
        id: a.id,
        ...assetToTimelineDragElement(scale)(a),
      }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ assetsByLayers, transitions ])


  const layerIndex = getLayerIndexFromY({
    y: clientY + scrollTop,
    topOffset: timelineTopOffset,
    layersLength: timelineDragElementsLayers.length,
  })

  const hoveredAssetIndex = timelineDragElementsLayers[layerIndex].findIndex(asset => asset.id === hoveredAssetId)
  const hoveredAsset = hoveredAssetIndex !== -1 ? timelineDragElementsLayers[layerIndex][hoveredAssetIndex] : null

  const hasRightCrossingTransition = useMemo(() => (
    transitions.find(tr => tr.isMountBetween && tr.isAttachedToLeft(hoveredAsset?.id))
  ), [ hoveredAsset, transitions ])

  if (ignoredTypes.includes(item.type) || isOverNewLayer) return null

  const offset = timelineLeftOffset - scrollLeft
  const x = clientX - offset

  // insertBetween - only for multiple
  const { selectedDragIds, selectedItems } = item ?? {}
  if ((selectedDragIds?.length ?? 0) > 1 || (selectedItems?.length ?? 0) > 1) {
    if (insertBetween) {
      setDraggingItemProps({ allowDropOnHover: true })
      return (
        <OverlayBoth
          x={time2Pixel(insertBetween.startTime, scale) + offset}
          y={snapToLayer({ layerIndex: insertBetween.layerIndex, timelineScrollTop: scrollTop }) + 1}
          width={20}
        />
      )
    }
    return null
  }

  if (!hoveredAsset) return null

  let defaultActiveOffset = Math.min(0.5 * hoveredAsset.width, 10)
  const hoveredAssetX = hoveredAsset.x + offset
  const hoveredAssetY = snapToLayer({ layerIndex, timelineScrollTop: scrollTop }) + 1
  let showBoth = false

  const leftSideAsset = timelineDragElementsLayers[layerIndex][hoveredAssetIndex - 1]
  const rightSideAsset = timelineDragElementsLayers[layerIndex][hoveredAssetIndex + 1]

  defaultActiveOffset = Math.min(
    defaultActiveOffset,
    0.5 * (leftSideAsset?.width || 20),
    0.5 * (rightSideAsset?.width || 20)
  )

  if (x < (hoveredAsset.x + defaultActiveOffset)) {
    if (leftSideAsset) {
      showBoth = Math.round(hoveredAsset.x - (leftSideAsset.x + leftSideAsset.width)) === 0
      if (showBoth) {
        const leftAssetRightTransition = transitions.find(tr => tr.isAttachedToLeft(leftSideAsset.id))
        const rightAssetLeftTransition = transitions.find(tr => tr.isAttachedToRight(hoveredAssetId))

        if (leftAssetRightTransition || rightAssetLeftTransition) {
          setDraggingItemProps({ allowDropOnHover: false })
          return null
        }

        setDraggingItemProps({ allowDropOnHover: true,
          detachedMode: true,
          normalizedSourceX: hoveredAsset.x,
          deltaOnDrop: width })

        return (
          <OverlayBoth
            x={hoveredAssetX}
            y={hoveredAssetY}
            width={defaultActiveOffset}
          />
        )
      }
    }
    if (hoveredAsset.x === 0) {
      setDraggingItemProps({ allowDropOnHover: true,
        detachedMode: true,
        normalizedSourceX: hoveredAsset.x,
        deltaOnDrop: width })

      return (
        <Overlay
          x={hoveredAssetX}
          y={hoveredAssetY}
          width={defaultActiveOffset}
          direction="left"
        />
      )
    }
  } else if (x > (hoveredAsset.x + hoveredAsset.width - defaultActiveOffset)) {
    if (rightSideAsset) {
      showBoth = Math.round(rightSideAsset.x - (hoveredAsset.x + hoveredAsset.width)) === 0

      if (showBoth) {
        const leftAssetRightTransition = transitions.find(tr => tr.isAttachedToLeft(hoveredAssetId))
        const rightAssetLeftTransition = transitions.find(tr => tr.isAttachedToRight(rightSideAsset.id))

        if (leftAssetRightTransition || rightAssetLeftTransition) {
          setDraggingItemProps({ allowDropOnHover: false })
          return null
        }

        setDraggingItemProps({ allowDropOnHover: true,
          detachedMode: true,
          normalizedSourceX: hoveredAsset.x + hoveredAsset.width,
          deltaOnDrop: rightSideAsset.x - (hoveredAsset.x + hoveredAsset.width) >= width
            ? 0 : width - (rightSideAsset.x - (hoveredAsset.x + hoveredAsset.width)) })

        return (
          <OverlayBoth
            x={hoveredAssetX + hoveredAsset.width}
            y={hoveredAssetY}
            width={defaultActiveOffset}
          />
        )
      }
    }
  }

  const endXHoverAsset = hoveredAsset.x + hoveredAsset.width
  const dragItemEndX = normalizedSourceX + draggingAssetWidth
  const endHoveredAsset = hoveredAsset.x + hoveredAsset.width

  if (!detachedMode && isEmpty(hoveredTransitions) && !hasRightCrossingTransition
    && (Math.floor(endXHoverAsset) <= Math.floor(normalizedSourceX)
    || Math.floor(dragItemEndX) <= Math.floor(hoveredAsset.x))) {
    setDraggingItemProps({
      allowDropOnHover: true,
      normalizedSourceX: normalizedSourceX >= endHoveredAsset
        ? endHoveredAsset
        : normalizedSourceX,
    })
  } else if (!isEmpty(hoveredTransitions) && detachedMode) {
    setDraggingItemProps({ allowDropOnHover: false })
  }

  return null
}

function OverlayBoth({ x, y, width }: { x: number, y: number, width: number }) {
  return (
    <div style={{ display: 'flex',
      position: 'absolute',
      transform: `translate(${x - width}px, ${y}px)` }}
    >
      <div
        className={cx(styles.item, styles.rightItem, styles.both)}
        style={{ width: `${width}px` }}
      />
      <div
        className={cx(styles.item, styles.leftItem, styles.both)}
        style={{ width: `${width}px` }}
      />
    </div>
  )
}

function Overlay({ x, y, width, direction }: { x: number, y: number, width: number, direction: 'left' | 'right' }) {
  return (
    <div
      className={cx(styles.item, { [styles.leftItem]: direction === 'left',
        [styles.rightItem]: direction === 'right' })}
      style={{ position: 'absolute',
        width: `${width}px`,
        transform: `translate(${x}px, ${y}px)` }}
    />
  )
}

export default DropHoverOnAssets
